// [목적] 사이드바 내비게이션 + 홈 통계 카드에서 쓰는 공용 라인 아이콘 세트
// [이유] 2026-09-20 디자인 QA: 기존 이모지 글리프(⌂✦⊕🎈❑◐⌕🔔⚙📔❤️⑂)는 OS/폰트별 렌더 편차가 크고
//        브랜드 톤과 굵기가 제각각이었다. 동일 스트로크(1.8px)·동일 라운드 캡의 24x24 라인 아이콘으로
//        통일해 시각적 일관성을 확보한다. 색상은 항상 currentColor로 상속해(#6-3 디자인 토큰 강제) 호출부의
//        기존 색 토큰(INK_HI/INK_MID/BRAND_TEXT_STRONG 등)을 그대로 따른다 — 이 파일에 색을 하드코딩하지 않는다.
// [사이드 이팩트] 없음(순수 표시 컴포넌트, 기능/상태 로직 무관). Sidebar.tsx/HomePage.tsx 에서만 사용.
// [수정 시 고려사항] 새 아이콘 추가 시 LINE_ICON_PATHS 에 등록 + LineIconName 유니온에 키 추가.
//                   path 좌표는 디자인 토큰 대상(#hex/px 금지)이 아닌 순수 아이콘 지오메트리이므로 예외.

import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';

export type LineIconName =
  | 'home'
  | 'chat'
  | 'explore'
  | 'festivals'
  | 'folio'
  | 'profile'
  | 'search'
  | 'notifications'
  | 'settings'
  | 'stat-folio'
  | 'stat-like'
  | 'stat-fork'
  // [2026-09-20] FolioPage 액션 바 글리프 교체용 — 기존 '⑂'(Fork) · '⬳'(Day 가져오기) 는
  // Pretendard 에 글리프가 없어 시스템 폴백으로 두부(□)나 무관한 모양으로 렌더됐다.
  | 'fork'
  | 'download'
  // [2026-09-20] 폴리오 메타 줄의 조회수 글리프('👁') 교체용 — 이모지는 OS/플랫폼마다 컬러 그림으로
  // 렌더돼(애플: 파란 눈동자) 모노 캡션 한 줄 안에서 혼자 튀고, 라인 아이콘 세트와 굵기도 맞지 않았다.
  | 'eye'
  // [2026-09-20] CongestionForecastCard "가장 한산" 배지의 '🕊'(비둘기 이모지) 교체용 —
  // 이모지는 플랫폼마다 컬러 그림이고, 의미도 모호했다(평화 = 한산?). 혼잡도 곡선의 최저점을
  // 가리키는 카드이므로 하강 추세 아이콘이 내용과 직결된다.
  | 'trend-down'
  // [2026-09-21] FolioEditPage 디자인 개편 — 기존 이모지 글리프('⋮⋮' '🔓' '🔒' '🗑')는 폰트/OS 별
  // 렌더 편차가 크고 라인 아이콘 세트와 굵기가 맞지 않아 편집 화면에서만 톤이 튀었다.
  | 'grip'
  | 'lock'
  | 'unlock'
  | 'trash'
  | 'clock'
  | 'wallet'
  | 'plus';

const LINE_ICON_PATHS: Record<LineIconName, string> = {
  home: 'm3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1Z',
  chat: 'M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M17 3l4 4M10 14l1-4 7-7a1.4 1.4 0 0 1 2 0l1 1a1.4 1.4 0 0 1 0 2l-7 7Z',
  explore: 'M12 3a9 9 0 1 0 .001 18.001A9 9 0 0 0 12 3Zm4 5-2.5 5.5L8 16l2.5-5.5Z',
  festivals:
    'm3 21 4-12 8 8-12 4ZM6 13l5 5M13 11l3-3M16 3v2M21 8h-2M20 3l-1 1M10 3c2 2-1 3 1 5M17 13c2-2 3 1 5-1',
  folio: 'M7 3h13a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H7ZM4 6v14a1 1 0 0 0 1 1h11M11 8h5M11 12h5',
  profile: 'M12 7a4 4 0 1 0 .001 8.001A4 4 0 0 0 12 7ZM4 21v-2a8 8 0 0 1 16 0v2',
  search: 'M10.5 4a6.5 6.5 0 1 0 .001 13.001A6.5 6.5 0 0 0 10.5 4Zm5.5 12 5 5',
  notifications: 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4',
  settings:
    'm10 3-.6 2-2 .9-1.9-.5-2 3.4L5 10.3v2.4l-1.5 1.5 2 3.4 1.9-.5 2 .9.6 2h4l.6-2 2-.9 1.9.5 2-3.4-1.5-1.5v-2.4l1.5-1.5-2-3.4-1.9.5-2-.9L14 3ZM12 11.5a3 3 0 1 0 .001 6.001A3 3 0 0 0 12 11.5Z',
  'stat-folio':
    'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM9 3v18M12 8h4M12 12h4',
  'stat-like':
    'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z',
  'stat-fork':
    'M6 5a2 2 0 1 0 .001 4.001A2 2 0 0 0 6 5Zm12 0a2 2 0 1 0 .001 4.001A2 2 0 0 0 18 5ZM6 19a2 2 0 1 0 .001 4.001A2 2 0 0 0 6 19ZM6 7v10M18 7v2a5 5 0 0 1-5 5H6',
  fork: 'M6 5a2 2 0 1 0 .001 4.001A2 2 0 0 0 6 5Zm12 0a2 2 0 1 0 .001 4.001A2 2 0 0 0 18 5ZM6 19a2 2 0 1 0 .001 4.001A2 2 0 0 0 6 19ZM6 7v10M18 7v2a5 5 0 0 1-5 5H6',
  download: 'M12 3v12M7.5 10.5 12 15l4.5-4.5M4 19h16',
  // 눈 윤곽(좌우 대칭 렌즈 형태) + 동공 — 다른 아이콘과 동일하게 1.8px 스트로크로 렌더된다.
  'trend-down': 'M3 7.5 9.5 14l3.5-3.5L21 18M21 18v-5.5M21 18h-5.5',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Zm9.5-2.6a2.6 2.6 0 1 0 .001 5.201A2.6 2.6 0 0 0 12 9.4Z',
  // 드래그 핸들 — strokeLinecap=round 이므로 길이 0 세그먼트(h.01)가 정원(dot)으로 렌더된다.
  grip: 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  lock: 'M6 11h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Zm2 0V7a4 4 0 0 1 8 0v4',
  unlock:
    'M6 11h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Zm2 0V7a4 4 0 0 1 7.5-2',
  trash:
    'M4 7h16M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M6.5 7l.9 12a1 1 0 0 0 1 .9h7.2a1 1 0 0 0 1-.9l.9-12M10 11v5M14 11v5',
  clock: 'M12 4a8 8 0 1 0 .001 16.001A8 8 0 0 0 12 4Zm0 3.8V12l2.8 1.8',
  wallet:
    'M4 8a2 2 0 0 1 2-2h11a1 1 0 0 1 1 1v1M4 8v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a1 1 0 0 0-1-1H6a2 2 0 0 1-2-2Zm12.5 6h.01',
  plus: 'M12 5v14M5 12h14',
};

interface LineIconProps {
  name: LineIconName;
  /** 정사각 픽셀 크기(테두리 상자) — 내부 stroke 두께는 크기와 무관하게 1.8 고정 */
  size?: number;
  'aria-hidden'?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * [목적] LINE_ICON_PATHS 를 24x24 뷰박스의 인라인 SVG로 렌더 — 항상 currentColor 스트로크
 * [이유] 호출부가 이미 Box/Typography 의 color sx 로 토큰 색을 지정하므로, 아이콘도 별도 색 prop 없이
 *        부모 색을 그대로 상속하게 해 색상 일관성을 강제한다(잘못된 하드코딩 방지).
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] fill 대신 stroke 기반 — 채워진 형태가 필요하면 새 아이콘 이름으로 별도 path 추가.
 */
export function LineIcon({ name, size = 18, sx, ...rest }: LineIconProps): JSX.Element {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={rest['aria-hidden'] ?? true}
      sx={{ display: 'block', flexShrink: 0, ...sx }}
    >
      <path d={LINE_ICON_PATHS[name]} />
    </Box>
  );
}
